import {Injectable} from '@angular/core';
import {DcpApiService, HttpRequestOptions} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Address, B2BUnit, B2BUser, BaseSiteService, Occ} from '@spartacus/core';
import {Observable, ReplaySubject, throwError} from 'rxjs';
import {catchError, first, map, shareReplay, switchMap} from 'rxjs/operators';
import {AddressIdentity} from '../model/dcp-address-identity';
import {DcpUserAddressService} from './user/dcp-user-address.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class B2bUnitService {
  private selectedItContactUid$: ReplaySubject<string> = new ReplaySubject<string>(1);
  private selectedInvoiceRecipientUid$: ReplaySubject<string> = new ReplaySubject<string>(1);
  private selectedShipToAddress: ReplaySubject<AddressIdentity> = new ReplaySubject<AddressIdentity>(1);
  private selectedBillToAddress: ReplaySubject<AddressIdentity> = new ReplaySubject<AddressIdentity>(1);

  constructor(private dcpApiService: DcpApiService, private baseSiteService: BaseSiteService, private userAddressService: DcpUserAddressService) {
    this.selectedItContactUid$.next(undefined);
    this.selectedInvoiceRecipientUid$.next(undefined);
    this.selectedShipToAddress.next(undefined);
    this.selectedBillToAddress.next(undefined);
  };

  getB2bUnitByUserAndB2bUnitId(userId: string, orgunitId: string): Observable<B2BUnit> {
    return this.baseSiteService.getActive().pipe(
      first(),
      switchMap((baseSite: string) =>
        this.dcpApiService.callKyma<B2BUnit>('orgunit', 'orgUnits', {
          baseSite,
          userId,
          orgunitid: orgunitId
        })
      ),
      shareReplay(),
      catchError((e) => throwError(e)),
      untilDestroyed(this)
    );
  }

  getOrgUnitAddresses(userId: string, orgUnitId: string): Observable<Address[]> {
    return this.baseSiteService.getActive().pipe(
      first(),
      switchMap((baseSite: string) =>
        this.dcpApiService.callKyma<B2BUnit>('orgunit', 'addresses', {baseSite, userId, orgunitid: orgUnitId})),
      map(object => object.addresses),
      shareReplay(),
      catchError((e) => throwError(e)),
      untilDestroyed(this)
    );
  }

  getAvailableUsersForUserIdAndUnitId(userId: string, orgunitId: string): Observable<B2BUser[]> {
    return this.baseSiteService.getActive().pipe(
      first(),
      switchMap((baseSite: string) =>
        this.dcpApiService.callKyma<Occ.OrgUnitUserList>('orgunit', 'availableUsers', {
          baseSite,
          userId,
          orgunitid: orgunitId
        }).pipe(
          map((response: Occ.OrgUnitUserList) => response.users),
          shareReplay(),
          catchError((e) => throwError(e))
        )
      ),
      untilDestroyed(this)
    );
  }

  sendTaxInfo(options: HttpRequestOptions): Observable<any> {
    return this.dcpApiService.callKyma<B2BUnit>('registration', 'configRegistration', {}, options, 'POST');
  }

  setCheckoutItContact(selectedItContactUid: string): void {
    this.selectedItContactUid$.next(selectedItContactUid);
  }

  getCheckoutItContact(): Observable<string> {
    return this.selectedItContactUid$.asObservable();
  }

  setCheckoutShipTo(selectedShipToAddress: Address): void {
    this.selectedShipToAddress.next(this.userAddressService.getAddressIdentity(selectedShipToAddress));
  }

  getCheckoutShipTo(): Observable<AddressIdentity> {
    return this.selectedShipToAddress.asObservable();
  }

  setCheckoutBillTo(selectedBillToAddress: Address): void {
    this.selectedBillToAddress.next(this.userAddressService.getAddressIdentity(selectedBillToAddress));
  }

  getCheckoutBillTo(): Observable<AddressIdentity> {
    return this.selectedBillToAddress.asObservable();
  }

  setCheckoutInvoiceRecipient(selectedItContactUid: string): void {
    this.selectedInvoiceRecipientUid$.next(selectedItContactUid);
  }

  getCheckoutInvoiceRecipient(): Observable<string> {
    return this.selectedInvoiceRecipientUid$.asObservable();
  }
}
